import { faBell } from "@fortawesome/free-solid-svg-icons/faBell";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons/faCircleUser";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { faLifeRing } from "@fortawesome/free-solid-svg-icons/faLifeRing";
import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Tooltip, Badge, Space } from "../../utils/local-antd";
import type { MenuProps } from "../../utils/local-antd";

import { Images } from "../../assets";
import { AtiraDropdown } from "../../components/AtiraDropdown";
import { AtiraIcon } from "../../components/AtiraIcon";
import { Button } from "../../components/Button";
import { CopyButton } from "../../components/CopyButton";
import { Flex } from "../../components/Flex";
import { AtiraImage } from "../../components/Image";
import { FontSize, Text } from "../../components/Text";
import { TextWithTruncated } from "../../components/TextWithTruncated";
import { Lengths } from "../../model/shared/enum/Lengths.enum";
import { UserRoles } from "../../model/user/types/UserRoles";
import { adminSliceSelectors } from "../../redux/admin/admin.selector";
import { adminActions } from "../../redux/admin/admin.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { userActions } from "../../redux/user/user.slice";
import { TestIDs } from "../../tests/types/testIds";
import { Spacing } from "../../theme/Spacing";
import { NotificationsDrawer } from "./components/NotificationsDrawer";
import { faCrown } from "@fortawesome/free-solid-svg-icons/faCrown";
import { Breakpoints } from "../../theme/Breakpoints";
import { SearchModal } from "./components/SearchModal";
import { TFunction } from "i18next";
import { User } from "../../model/user/User";
import { SettingsSubRoutes } from "../../model/app/types/SettingsSubRoutes.enum";
import { SearchBar } from "./components/SearchBar";
import { ImageCreateEdit } from "../../components/ImageCreateEdit";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import { useTheme } from "../../ThemeContext";
import { PageIcons } from "../../utils/GlobalIcons";
import { UserProfileWrapper } from "../../components/UserProfileWrapper";
import { isImpersonation } from "../../redux/store-utils";

const Container = styled(Flex)`
  /* Changing the hight of the header requires updating the OutletWrapper's 'height' and 'margin-top' in Layout.tsx
     and the '.ant-menu' class's 'top' and 'height' in SideMenu.tsx
  */

  height: 3.25rem;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  background-color: ${({ theme }) => theme.main};
`;

const LogoWrapper = styled(Flex)`
  align-items: center;
  width: calc(190px - ${Spacing.m});
`;

const SettingsWrapper = styled(Flex)`
  align-items: center;
  justify-content: flex-start;
  flex-direction: row-reverse;
  margin-inline-end: ${Spacing.m};
  /* Was a fixed 30rem, which forced the header wider than a split-screen
     viewport. Let it shrink; the icons stay, the name truncates. */
  max-width: 30rem;
  flex: 0 1 auto;
  min-width: 0;
  gap: ${Spacing.l};

  @media (max-width: ${Breakpoints.DESKTOP}) {
    gap: ${Spacing.s};
  }
`;

const HeaderUserName = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: ${Breakpoints.TABLET}) {
    display: none;
  }
`;

const StyleLink = styled(Link)`
  text-decoration: none;
  color: white;

  &:hover {
    color: white;
  }
`;

const StyledText = styled(Text)`
  white-space: wrap;
  text-overflow: wrap;
  word-break: break-all;
  color: ${({ theme }) => theme.black};
`;

const StyledHr = styled.hr`
  height: 1px;
  border: 0;
  background-color: ${({ theme }) => theme.darkerSub};
`;

const LogoutButton = styled(Button)`
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
  border: ${({ theme }) => `0.1rem solid ${theme.main}`};
  width: 100%;
`;

const StyledCopyButton = styled(CopyButton)`
  padding: 0;
  margin: 0;
`;

const StyledBadge = styled(Badge)`
  display: flex;
`;

const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};

  svg {
    width: 1rem;
  }
`;

const EmailWrapper = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  svg {
    width: 1rem;
  }
`;

const IconLink = styled(Link)`
  color: ${({ theme }) => theme.sub};
  text-decoration: none;

  svg {
    font-size: calc(${FontSize.l} + 0.25rem);
  }

  &:hover {
    color: ${({ theme }) => theme.sub};
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    svg {
      font-size: ${FontSize.l};
    }
  }

  /* A real hit area for touch - the glyph alone is ~24px. */
  @media (hover: none) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.5rem;
    min-height: 2.5rem;
  }
`;

const IconButton = styled(Button)`
  background-color: transparent;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.sub};

  svg {
    font-size: calc(${FontSize.l} + 0.25rem);
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    svg {
      font-size: ${FontSize.l};
    }
  }

  @media (hover: none) {
    padding: ${Spacing.s};
  }
`;

const SearchButton = styled(IconButton)`
  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: none;
  }
`;

const getProfileMenuItems = (
  t: TFunction,
  onLogout: VoidFunction,
  user: User,
  isImpersonating: boolean,
) =>
  [
    {
      key: "g1",
      type: "group",
      children: [
        {
          key: "1",
          label: (
            <MenuLink to={"/profile"} style={{ cursor: "pointer" }}>
              <AtiraIcon icon={PageIcons.Profile} color="main" size="xl" />
              <StyledText>{t("common.profile")}</StyledText>
            </MenuLink>
          ),
        },
        {
          key: "2",
          label: user?.email ? (
            <Flex justifyContent="space-between" alignItems="center" gap="s">
              <EmailWrapper>
                <AtiraIcon icon={faEnvelope} color="main" size="xl" />
                <TextWithTruncated length={18}>{user.email}</TextWithTruncated>
              </EmailWrapper>

              <StyledCopyButton
                copy={user.email}
                iconColor="main"
                iconSize="xl"
              />
            </Flex>
          ) : (
            <StyledText>{t("common.user_email")}</StyledText>
          ),
          className: "user-email",
        },
        {
          key: "3",
          label: (
            <MenuLink to={"/permissions"}>
              <AtiraIcon icon={PageIcons.Permissions} color="main" size="xl" />
              <StyledText>{t("header.dropdown.permissions")}</StyledText>
            </MenuLink>
          ),
        },
        {
          key: "4",
          label: (
            <MenuLink to={"/help-support"}>
              <AtiraIcon icon={faLifeRing} color="main" size="xl" />
              <StyledText>{t("common.help_support")}</StyledText>
            </MenuLink>
          ),
        },
      ],
    },
    {
      key: "6",
      label: <StyledHr />,
      className: "hr",
    },
    ...(isImpersonating
      ? []
      : [
          {
            key: "7",
            label: (
              <LogoutButton
                hover
                dataTestId={TestIDs.auth.logout.submit}
                onClick={onLogout}
              >
                {t("header.logout")}
              </LogoutButton>
            ),
            className: "logout-button",
          },
        ]),
  ] as MenuProps["items"];

export const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useTheme();

  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const unreadMessages = useAppSelector(
    adminSliceSelectors.selectAdminMessages,
  )?.filter(({ read }) => read === false);

  const adminMessagesPage = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPage,
  );
  const adminMessagesPageSize = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPageSize,
  );

  const isStaging = process.env.REACT_APP_ENV === "staging";

  const onLogout = () => {
    dispatch(userActions.logoutAndClearPersistence());
  };

  useEffect(() => {
    if (isEmpty(loggedInUser)) {
      navigate("/", { replace: true });
    }
  }, [loggedInUser, navigate]);

  useEffect(() => {
    dispatch(
      adminActions.getAdminMessages({
        userId: loggedInUser?._id!,
        meta: { page: adminMessagesPage - 1, count: adminMessagesPageSize },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loggedInUser?._id]);

  return (
    <Container>
      <LogoWrapper>
        <StyleLink to={"/"}>
          <AtiraImage src={Images.AtiraSpaceLogoBetaSub} width="100%" />
        </StyleLink>
      </LogoWrapper>

      <SearchBar />

      {isStaging ? (
        <Text color="danger" fontSize="l">
          /\ STAGING ENV /\
        </Text>
      ) : null}

      <SettingsWrapper>
        <Space orientation="vertical">
          <Flex alignItems="center" gap="s">
            <HeaderUserName>
              <TextWithTruncated length={Lengths.NAME} color="white">
                {loggedInUser?.name as string}
              </TextWithTruncated>
            </HeaderUserName>

            <AtiraDropdown
              items={getProfileMenuItems(
                t,
                onLogout,
                loggedInUser,
                isImpersonation(),
              )}
              placement="bottomRight"
            >
              <UserProfileWrapper>
                {loggedInUser?.picture ? (
                  <ImageCreateEdit src={loggedInUser.picture} />
                ) : (
                  <AtiraIcon icon={faCircleUser} size="3x" color="sub" />
                )}
              </UserProfileWrapper>
            </AtiraDropdown>
          </Flex>
        </Space>

        {loggedInUser?.roles?.includes(UserRoles.ADMIN) ? (
          <Tooltip title={t("admin.header.title")}>
            <IconLink to={"/admin"}>
              <AtiraIcon icon={PageIcons.Admin} />
            </IconLink>
          </Tooltip>
        ) : null}

        <Tooltip title={t("common.notifications")}>
          <StyledBadge
            count={unreadMessages?.length}
            color={unreadMessages?.length ? theme.main : theme.transparent}
          >
            <IconButton onClick={() => setOpen(true)} icon={faBell} />
          </StyledBadge>
        </Tooltip>

        <NotificationsDrawer open={open} onClose={() => setOpen(false)} />

        <Tooltip title={t("common.settings")}>
          <IconButton
            onClick={() => navigate(`/settings/${SettingsSubRoutes.GENERAL}`)}
            icon={faGear}
            iconWidth="2x"
            dataTestId={TestIDs.general.header.settings}
          />
        </Tooltip>

        <Tooltip title={t("header.subscription")}>
          <IconLink to={"/subscription"}>
            <AtiraIcon icon={faCrown} />
          </IconLink>
        </Tooltip>

        <Tooltip>
          <SearchButton
            icon={faSearch}
            iconWidth="2x"
            onClick={() => setSearchModalVisible(true)}
          />
        </Tooltip>
      </SettingsWrapper>

      <SearchModal
        open={searchModalVisible}
        onClose={() => setSearchModalVisible(false)}
      />
    </Container>
  );
};
